import { DOCUMENT } from '@angular/common';
import { afterNextRender, Component, DestroyRef, ElementRef, HostListener, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../_services/auth.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  host: { class: 'cir-home-page' },
})
export class HeaderComponent {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly intelligenceBarVisible = signal(true);
  readonly menuOpen = signal(false);
  readonly scrolled = signal(false);
  readonly isLoggedIn = signal(false);

  constructor() {
    // Match the server's guest markup until hydration has finished.
    afterNextRender(() => {
      this.auth.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.isLoggedIn.set(this.auth.isLoggedIn());
      });
    });
    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationEnd) this.closeMenu();
    });
  }

  @HostListener('window:scroll')
  onScroll(): void { this.scrolled.set((this.document.defaultView?.scrollY ?? 0) > 40); }

  @HostListener('window:resize')
  onResize(): void {
    if ((this.document.defaultView?.innerWidth ?? 0) > 1200) this.closeMenu();
  }

  @HostListener('document:keydown.escape')
  closeMenu(): void {
    this.menuOpen.set(false);
    this.host.nativeElement.querySelector('details.account-menu')?.removeAttribute('open');
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.closeMenu();
  }

  toggleMenu(): void { this.menuOpen.update(open => !open); }
  dismissIntelligenceBar(): void { this.intelligenceBarVisible.set(false); }

  scrollToTop(): void {
    this.closeMenu();
    const win = this.document.defaultView;
    win?.scrollTo({ top: 0, behavior: win.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  logout(): void {
    this.closeMenu();
    this.auth.logout();
  }
}
